import { ObjectId } from 'bson';
import { computed, makeObservable } from 'mobx';
import type { Moment } from 'moment';
import type { WorkBook, WorkSheet } from 'xlsx';
import { utils } from 'xlsx';

import { concatPath, moment } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { isWretchError } from '@feathr/rachis';
import { Collection, DisplayModel, wretch } from '@feathr/rachis';

import type { CustomField, CustomFields, FieldDataType } from './custom_fields';
import type { IPerson } from './persons';

/**
 * `pending` is queued, waiting to be picked up. `processing` is a run in progress: the new importer
 * moves an import into it as the run starts and out of it at the end (Feathr/anhinga#9328). A legacy
 * run stays `pending` for its whole run, so treat both as running.
 */
export type TImporterState = 'draft' | 'pending' | 'processing' | 'complete' | 'failed';

/*
 * What an address looks like for the header question. The browser is the only
 * side that asks it: the server reads row 1 as data when the saved column_names
 * are the synthesized `Column N` names, rather than re-running this test
 * (Feathr/anhinga#9207).
 */
const EMAIL_PATTERN = /^[^@]+@[^@.]+\.[^@]+$/;

export interface IParsedWorkbook {
  columnNames: string[];
  // Cells as SheetJS reads them: strings, but also numbers, booleans and dates.
  rows: unknown[][];
}

export interface IParseWorkbookOptions {
  /*
   * Whether a row 1 that looks like data stays a data row. The caller decides,
   * from the imports revamp flags, because only the new importer (the account's
   * importOrchestrator flag) reads that row as data.
   */
  detectHeaderless?: boolean;
  /** Whether a heading that repeats an earlier one is renamed `name (2)`, `name (3)`. */
  renameRepeatedHeaders?: boolean;
  /** Whether a blank heading is named `Column N` after its position. */
  nameBlankHeaders?: boolean;
}

/*
 * Not translated on purpose. These names are saved to the import record as
 * column_names and matched against the mapping the server resolves, so they have
 * to be one fixed string on both ends rather than whatever locale the person who
 * uploaded the file happened to be in.
 */
export function synthesizedColumnName(index: number): string {
  return `Column ${index + 1}`;
}

/*
 * The upload step has no column mapping yet, so it cannot ask whether the mapped
 * Email cell holds an address. It asks whether any text cell in row 1 does: a
 * header row never carries an address. A headerless file whose first contact has
 * no address in row 1 still reads as headered and loses that row; looking at
 * row 2 as well would misread every file that does have a header row.
 */
function looksLikeData(row: unknown[]): boolean {
  return row.some((cell) => typeof cell === 'string' && EMAIL_PATTERN.test(cell.trim()));
}

/*
 * A row is only as long as its last non-empty cell, so a first contact with a
 * blank last field parses short. Size the synthesized names to the sheet's used
 * range instead, so every column can be mapped; the server pads rows to the
 * saved width and cannot recover a missing one. sheet_to_json bounds every row by
 * the same range, so no row is wider.
 */
function sheetWidth(sheet: WorkSheet): number {
  const ref = sheet['!ref'];
  if (!ref) {
    return 0;
  }
  const range = utils.decode_range(ref);
  return range.e.c - range.s.c + 1;
}

function sheetToRows(sheet: WorkSheet): unknown[][] {
  return utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
}

/*
 * Splits an uploaded workbook into column names and data rows. Row 1 is the
 * column names, unless detectHeaderless is on and row 1 looks like data: then a
 * file that starts straight into data keeps its first contact as a row, and the
 * columns get positional names. See Feathr/anhinga#8619.
 */
export function parseWorkbook(
  workbook: WorkBook,
  {
    detectHeaderless = false,
    renameRepeatedHeaders = false,
    nameBlankHeaders = false,
  }: IParseWorkbookOptions = {},
): IParsedWorkbook {
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName === undefined ? undefined : workbook.Sheets[sheetName];
  if (!sheet) {
    return { columnNames: [], rows: [] };
  }

  const data = sheetToRows(sheet);
  const firstRow = data[0] ?? [];

  if (detectHeaderless && looksLikeData(firstRow)) {
    return {
      columnNames: Array.from({ length: sheetWidth(sheet) }, (_, index) =>
        synthesizedColumnName(index),
      ),
      rows: data,
    };
  }

  // Array.from, unlike .map, visits the hole a blank heading leaves in the row.
  return {
    columnNames: nameHeaderCells(Array.from(firstRow, cellText), {
      nameBlankHeaders,
      renameRepeatedHeaders,
    }),
    rows: data.slice(1),
  };
}

// Not translated: the server resolves mappings against the saved name.
export function repeatedColumnName(columnName: string, occurrence: number): string {
  return `${columnName} (${occurrence})`;
}

function claimRepeatName(
  columnName: string,
  taken: Set<string>,
  nextOccurrence: Map<string, number>,
): string {
  for (let occurrence = nextOccurrence.get(columnName) ?? 2; ; occurrence += 1) {
    const renamed = repeatedColumnName(columnName, occurrence);
    if (!taken.has(renamed)) {
      taken.add(renamed);
      nextOccurrence.set(columnName, occurrence + 1);
      return renamed;
    }
  }
}

// The server matches a mapping's column by exact name, so `ID` and `id` are two columns.
function nameHeaderCells(
  cells: string[],
  {
    nameBlankHeaders,
    renameRepeatedHeaders,
  }: Required<Pick<IParseWorkbookOptions, 'nameBlankHeaders' | 'renameRepeatedHeaders'>>,
): string[] {
  const isSynthesized = cells.map((cell) => nameBlankHeaders && cell.trim() === '');
  const taken = new Set(cells.filter((_, index) => !isSynthesized[index]));
  const nextOccurrence = new Map<string, number>();
  const seen = new Set<string>();

  const named = cells.map((cell, index) => {
    if (isSynthesized[index]) {
      return cell;
    }
    if (!renameRepeatedHeaders || cell === '' || !seen.has(cell)) {
      seen.add(cell);
      return cell;
    }
    return claimRepeatName(cell, taken, nextOccurrence);
  });

  // Saved names of exactly `Column 1` to `Column N` tell the server that row 1 is data.
  const firstSynthesized = isSynthesized.indexOf(true);
  const wouldBePositional =
    firstSynthesized !== -1 &&
    named.every((name, index) => isSynthesized[index] || name === synthesizedColumnName(index));

  return named.map((name, index) => {
    if (!isSynthesized[index]) {
      return name;
    }
    const synthesized = synthesizedColumnName(index);
    if (taken.has(synthesized) || (wouldBePositional && index === firstSynthesized)) {
      return claimRepeatName(synthesized, taken, nextOccurrence);
    }
    taken.add(synthesized);
    return synthesized;
  });
}

/** Reads a sheet cell as text. */
export function cellText(cell: unknown): string {
  if (cell === undefined || cell === null) {
    return '';
  }
  if (typeof cell === 'string') {
    return cell;
  }
  if (typeof cell === 'number' || typeof cell === 'boolean' || cell instanceof Date) {
    return cell.toString();
  }
  return JSON.stringify(cell) ?? '';
}

export interface IOverride {
  feathr_attr: string;
  attr_type: string;
  value: string | boolean | number;
}

/** Marks every person in an import as opted out of all communications. */
export const OPT_OUT_OVERRIDE: Readonly<IOverride> = {
  feathr_attr: 'pp_opt_outs.all',
  attr_type: 'bool',
  value: true,
};

/**
 * A mapped cell the preview could not read. `unparseable_cell` is the value the field's type
 * refused; `unreachable_column` is a row shorter than the header. Only the orchestrator path
 * sends these, so an older response simply has none.
 */
export interface IPreviewProblem {
  column_name: string;
  /** For a field the account does not have yet, this is the key the customer typed. */
  feathr_attr: string;
  reason: 'unparseable_cell' | 'unreachable_column';
}

/** A cell whose symbols (`$`, commas) were stripped to read it as a number. */
export interface IPreviewCleanedCell {
  column_name: string;
  feathr_attr: string;
  original: string;
  value: number;
}

export interface IPreviewRow extends Record<string, unknown> {
  id: string;
  input: Record<string, string>;
  result?: Partial<IPerson>;
  error?: string;
  problems?: IPreviewProblem[];
  /** Absent on a response from before the API cleaned symbols; empty when nothing was cleaned. */
  cleaned?: IPreviewCleanedCell[];
}

/** One reason in an import's rejection breakdown. */
export interface IRejectionBreakdownLine {
  code: string;
  count: number;
  /** The sentence the error file uses for this reason. Null for a code the server retired. */
  message: string | null;
}

export interface IImporter extends IBaseAttributes {
  /** Rows the customer left empty. Not a rejection, and not part of `skipped`. */
  blank?: number;
  column_mappers: IImportColumn[];
  column_names: string[];
  created_by: string;
  data_key: string;
  data: string[];
  /** Rows that matched an existing record and updated it. */
  duplicates?: number;
  /** Rows that created a new record. */
  inserted?: number;
  /** Rows the run did not import. */
  skipped?: number;
  /** When the run ended, whether it completed or failed. Unset while running and on a draft. */
  date_completed?: string;
  /** When the worker began the run. Unset on a draft and on an import that has not started. */
  date_started?: string;
  event: string;
  /**
   * The API's machine-readable reason a failed import failed (Feathr/anhinga#8512). Unset on any
   * other state and on an import that failed before the API recorded one.
   */
  failure_code?: string | null;
  /**
   * The API's customer-facing sentence for the failure, safe to show verbatim. Unset on the same
   * imports as `failure_code`.
   */
  failure_message?: string | null;
  /** True while a run is going; `state` stays `pending`. Always false on the legacy importer. */
  is_running?: boolean;
  overrides?: IOverride[];
  file_name: string;
  kind: string;
  name?: string;
  /** Percent read, 0-100. Null until the run starts; capped at 99 until complete. */
  progress?: number | null;
  /**
   * How many rows each reason refused, most rows first, with the server's sentence for each.
   * Empty on an import that refused nothing and on one that ran before the tally was recorded.
   */
  rejection_breakdown?: IRejectionBreakdownLine[];
  /** Created rows whose email address the account already had under a different External ID. */
  second_records?: number | null;
  /** The server's sentence for `second_records`, read after the count. Null when there are none. */
  second_records_message?: string | null;
  segment: string;
  state: TImporterState;
  /** Cells on imported rows that could not be read and were left empty. Null on an older import. */
  unreadable_cells?: number | null;
  tag_ids: string[];
  errors_key?: string;
}

export interface IImportColumn {
  attr_type?: FieldDataType;
  column_name: string;
  feathr_attr?: string;
  u_key?: string;
}

export class Importer extends DisplayModel<IImporter> {
  public readonly className = 'Importer';

  public get constraints(): TConstraints<IImporter> {
    return {
      name: {
        presence: {
          allowEmpty: false,
          message: '^File name cannot be blank.',
        },
        length: {
          maximum: 45,
          tooLong: '^Import name must be less than 45 characters in length.',
        },
      },
      data_key: {
        presence: {
          allowEmpty: false,
          message: '^Please upload a file.',
        },
      },
      event: {
        presence: {
          allowEmpty: false,
        },
      },
      column_mappers: {
        presence: {
          allowEmpty: false,
        },
        length: () => {
          return { minimum: 1, message: '^You must have at least one column.' };
        },
        duplicates: {
          maximum: 0,
          message: "^You can't map multiple columns to the same field.",
          properties: ['feathr_attr'],
          ignoreEmpty: true,
        },
      },
    };
  }

  constructor(attributes: Partial<IImporter> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<IImporter> {
    return {
      column_mappers: [],
      column_names: [],
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    if (this.get('event') && this.get('kind') === 'partner') {
      return concatPath(`/projects/${this.get('event')}/partners/imports/${this.id}`, pathSuffix);
    }
    return concatPath(`/data/imports/${this.id}`, pathSuffix);
  }

  public getUsedCustomFields(customFields: CustomFields): CustomField[] {
    const defaults = [
      'name',
      'first_name',
      'last_name',
      'email',
      'emails',
      'date_of_birth',
      'external_id',
      'website',
      'description',
      'logo',
      'companies',
      'occupation',
      'pp_opt_outs.all',
      'phone',
      'income',
      'language',
      'address.administrative_area_name',
      'address.country_name',
      'address.postal_code',
      'address.locality',
      'address.premise1',
      'address.premise2',
      'address.timezone',
    ];
    return this.get('column_mappers')
      .filter((m: IImportColumn) => !!m.feathr_attr && !defaults.includes(m.feathr_attr))
      .map((m: IImportColumn) => customFields.get(m.feathr_attr!));
  }

  public async preview(columnMappers: IImportColumn[]): Promise<IPreviewRow[]> {
    if (!this.collection) {
      return [];
    }

    const body = JSON.stringify({
      column_mappers: columnMappers,
      column_names: this.get('column_names'),
      data_key: this.get('data_key'),
      file_name: this.get('file_name'),
      name: this.get('name'),
      rows: this.get('rows'),
    });

    const response = await wretch<IPreviewRow[]>(`${this.collection.url()}${this.id}/preview`, {
      method: 'POST',
      body,
      headers: this.collection.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data as IPreviewRow[];
  }

  public getEphemeralCustomFields(customFields: CustomFields): CustomField[] {
    return this.getUsedCustomFields(customFields).filter((cf) => cf.isEphemeral);
  }

  @computed
  public get dateCreated(): Moment {
    return moment.utc(new ObjectId(this.id).getTimestamp());
  }

  @computed
  public get downloadUrl(): string {
    if (this.collection) {
      return `${this.collection.url()}${this.id}/download`;
    }
    return '';
  }

  @computed
  public get downloadErrorUrl(): string {
    if (this.collection) {
      return `${this.collection.url()}${this.id}/download?file-type=error`;
    }
    return '';
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed Importer';
  }

  @computed
  public get completedColumnMappers(): IImportColumn[] {
    return this.get('column_mappers')
      .filter((m: IImportColumn) => m.feathr_attr)
      .filter((m: IImportColumn) => m?.u_key !== '')
      .sort();
  }

  @computed
  public get isOptOut(): boolean {
    return this.get('overrides', []).length > 0;
  }

  public setOptOut(optOut: boolean): void {
    const otherOverrides = this.get('overrides', []).filter(
      (override) => override.feathr_attr !== OPT_OUT_OVERRIDE.feathr_attr,
    );
    this.set({ overrides: optOut ? [...otherOverrides, { ...OPT_OUT_OVERRIDE }] : otherOverrides });
  }

  /** Turns a failed import back into a draft. */
  public resetFailedToDraft(): void {
    if (this.get('state') === 'failed') {
      this.set({ state: 'draft' });
    }
  }

  public async deleteImport(id: string): Promise<void> {
    this.assertCollection(this.collection);
    const url = `${this.collection.url()}${id}`;
    const response = await wretch<IImporter>(url, {
      headers: this.collection.getHeaders(),
      method: 'DELETE',
    });
    if (isWretchError(response)) {
      throw response.error;
    }
  }
}

export class Importers extends Collection<Importer> {
  public getClassName(): string {
    return 'importers';
  }

  public getModel(attributes: Partial<IImporter>): Importer {
    return new Importer(attributes);
  }

  public override isActiveForRefresh(model: Importer): boolean {
    return model.get('state') === 'pending' || Boolean(model.get('is_running'));
  }
}
